import {useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction} from 'react'

import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {
  isOrganizationEligibleForDefaultScope,
  isOrganizationSandboxEnabled,
  resolveDefaultSandboxScope,
} from '@github-ui/copilot-chat-helpers/sandbox-scope'

import {clearPendingSsoOrgLogin, PENDING_SSO_ORG_STORAGE_KEY} from './pending-sso-org'
import {hasPendingSsoChatTarget} from './pending-sso-target'
import {toPersistedScope, type AvailableOrganization, type TaskComposerScope} from './shared'

export interface TaskComposerScopeState {
  selectedScope: TaskComposerScope
  setSelectedScope: Dispatch<SetStateAction<TaskComposerScope>>
  setDerivedSelectedScope: Dispatch<SetStateAction<TaskComposerScope>>
  /** Owner preserved from a handoff when it cannot be represented by the picker. */
  launchScopeLogin?: string
  /** Retained owner that still requires SSO before repository-less submission. */
  blockedScopeLogin?: string
  /**
   * The org login an SSO redirect was started for, read once on mount.
   *
   * Callers that own the environment need it too: returning from the handshake
   * has to land back on Sandbox, which is the only place the scope applies.
   */
  pendingSsoOrgLogin: string | null
}

/**
 * Owns which organization a Sandbox draft is scoped to.
 *
 * It lives outside the task composer controller so a surface that switches
 * between destinations can keep one scope: the picker is offered for a Sandbox
 * Chat exactly as it is for a Sandbox task, and moving between them must not
 * silently reset the owner the repository picker filters by.
 *
 * The scope persists per account to `copilotLocalStorage` so a returning user
 * lands on their last explicit or SSO-restored choice. Persistence is only
 * meaningful when the composer can reach Sandbox, since that's the only
 * surface where the scope applies. Callers pass `enablePersistence=false`
 * (for example, actions-only surfaces that never show the ScopePicker) to
 * skip the initial read and explicit writes. A caller that supplies its own
 * scope passes an empty `availableOrganizations` list; that also suppresses
 * persistence here so a secondary composer instance can't clobber the
 * primary selection.
 *
 * `initialScopeLogin` seeds the scope from a handoff (for example, the owner
 * from `?repo=owner/name`). It resolves to personal when it matches
 * `currentUserLogin`, to an org when it matches an available org, and
 * otherwise falls through to persisted scope.
 * `pageOwner` defaults a repository-page draft ahead of launch-owner seeding.
 */
export function useTaskComposerScope(
  availableOrganizations: AvailableOrganization[],
  enablePersistence: boolean,
  initialScopeLogin?: string,
  currentUserLogin?: string,
  lastContributedOrganizationLogin?: string,
  retainPendingSsoOrgForChat = false,
  pageOwner?: string,
): TaskComposerScopeState {
  const persist = availableOrganizations.length > 0 && enablePersistence
  // After returning from an SSO redirect kicked off by the ScopePicker, restore
  // the scope to the org the user had picked and the Sandbox environment they
  // were on. The sessionStorage read is inlined here (rather than behind a
  // helper) because the linter rejects hidden non-reactive reads during
  // render. The value is only cleared after commit so a StrictMode-abandoned
  // first render can't swallow the one-shot handshake.
  const [pendingSsoOrgLogin] = useState(() => {
    try {
      return sessionStorage.getItem(PENDING_SSO_ORG_STORAGE_KEY)
    } catch {
      return null
    }
  })
  const restoredSsoOrg = useMemo(
    () =>
      pendingSsoOrgLogin
        ? (availableOrganizations.find(
            org =>
              org.login.toLowerCase() === pendingSsoOrgLogin.toLowerCase() &&
              isOrganizationEligibleForDefaultScope(org),
          ) ?? null)
        : null,
    [pendingSsoOrgLogin, availableOrganizations],
  )
  const [selectedScope, setSelectedScope] = useState<TaskComposerScope>(() => {
    const persistedScope =
      persist && currentUserLogin ? copilotLocalStorage.getTaskComposerScope(currentUserLogin) : null
    const hasChosenScope =
      persist && !persistedScope && copilotLocalStorage.hasChosenTaskComposerScope(currentUserLogin)
    const result = resolveDefaultSandboxScope({
      organizations: availableOrganizations,
      currentUserLogin,
      pendingSsoOrgLogin,
      pageOwner,
      launchOwner: initialScopeLogin,
      persistedScope,
      hasChosenScope,
      persistenceEnabled: persist,
      lastContributedOrganizationLogin,
    })
    return result.status === 'ready' ? result.scope : {kind: 'personal'}
  })
  const selectedScopeRef = useRef(selectedScope)
  const setDerivedSelectedScope = useCallback<Dispatch<SetStateAction<TaskComposerScope>>>(next => {
    const scope = typeof next === 'function' ? next(selectedScopeRef.current) : next
    selectedScopeRef.current = scope
    setSelectedScope(scope)
  }, [])
  const clearedPendingSsoOrgRef = useRef(false)
  useEffect(() => {
    if (!pendingSsoOrgLogin || clearedPendingSsoOrgRef.current) return
    if (retainPendingSsoOrgForChat && availableOrganizations.length === 0 && hasPendingSsoChatTarget()) return
    clearedPendingSsoOrgRef.current = true
    if (restoredSsoOrg && enablePersistence) {
      copilotLocalStorage.setTaskComposerScopeChosen(currentUserLogin)
      if (persist && currentUserLogin) {
        copilotLocalStorage.setTaskComposerScope(
          toPersistedScope({kind: 'organization', organization: restoredSsoOrg}),
          currentUserLogin,
        )
      }
    }
    clearPendingSsoOrgLogin()
  }, [
    availableOrganizations.length,
    currentUserLogin,
    enablePersistence,
    pendingSsoOrgLogin,
    persist,
    restoredSsoOrg,
    retainPendingSsoOrgForChat,
  ])

  // Revalidate lazy organization data without disrupting an in-progress SSO handshake.
  useEffect(() => {
    if (selectedScope.kind !== 'organization') return
    const match = availableOrganizations.find(org => org.login === selectedScope.organization.login)
    if (!match || !isOrganizationSandboxEnabled(match)) {
      setDerivedSelectedScope({kind: 'personal'})
    }
  }, [availableOrganizations, selectedScope, setDerivedSelectedScope])

  const selectScope = useCallback<Dispatch<SetStateAction<TaskComposerScope>>>(
    next => {
      const scope = typeof next === 'function' ? next(selectedScopeRef.current) : next
      selectedScopeRef.current = scope
      if (enablePersistence) copilotLocalStorage.setTaskComposerScopeChosen(currentUserLogin)
      if (persist && currentUserLogin) {
        copilotLocalStorage.setTaskComposerScope(toPersistedScope(scope), currentUserLogin)
      }
      setSelectedScope(scope)
    },
    [currentUserLogin, enablePersistence, persist],
  )

  return {
    selectedScope,
    setSelectedScope: selectScope,
    setDerivedSelectedScope,
    pendingSsoOrgLogin,
  }
}
